/*
  Restores the GLOBAL JSON-LD schema blocks that `pages/_app.tsx` rendered on
  every page before the SA-6876 metadata refactor:
    - WebSite       (old `siteSchema`)
    - Organization  (old `organizationSchema`)
    - WebPage       (old `{ ...pageSchema, url: canonicalUrl }`)

  These are the VERBATIM old global values (see the original
  `components/MetaData/LDJSON/schemas`), intentionally including the original
  `http://schema.org` context and `isFamilyFriendly: 'True'` so behavior matches
  exactly what shipped before.

  They are rendered only as a GLOBAL FALLBACK: each block is emitted only when
  the current page does NOT already provide its own (`metaData.schema.*`), using
  the same render key as the per-page MetaData, so the per-page (SA-6876) schema
  overrides the global one with no duplicate entities.
*/

import React, { FC } from 'react'
import Head from 'next/head'
import { NextRouter } from 'next/router'
import { createNewURL } from 'lib/storeLocale/client'
import { getGeoLocationMetaData } from 'selectors/user/cookie' // TODO - Move to Locale
import { getPagePath } from 'selectors/page'
import { urlQuery } from 'lib/helpers'
import parse from 'lib/url/parse'
import { getSafeHttpUrl } from 'lib/sanitize'
import { PageInitialProps, JsonLdSchema } from 'types/metadata'
import { User } from 'types/User'
import { Shared } from 'types/Shared'
import { Page } from 'types/Page'
import renderLDJSON from './LDJSON'

/* We need a way to have clean path for homepage ( Product ASK )
   Ex: `.com/` => we want `.com`
   This helper will only clean up specific rule
*/
const cleanHomePage = (url: string): string => {
  return url.match(/com[/]$/) ? url.replace(/\/$/, '') : url
}

// Verbatim old global values.
const siteSchema: JsonLdSchema = {
  '@context': 'http://schema.org',
  '@type': 'WebSite',
  name: 'saatchiart.com',
  alternateName: 'Saatchi Art',
  url: 'https://www.saatchiart.com',
}

const pageSchema: JsonLdSchema = {
  '@context': 'http://schema.org',
  '@type': 'WebPage',
  url: 'https://saatchiart.com',
  mainEntityOfPage: 'https://saatchiart.com',
  sameAs: [
    'https://www.youtube.com/Saatchiart',
    'https://www.facebook.com/saatchionline',
    'https://twitter.com/SaatchiArt',
    'https://www.instagram.com/saatchiart',
    'https://www.pinterest.com/saatchiart',
  ],
  keywords: ['original art', 'art', 'artworks', 'art for sale', 'gallery'],
  inLanguage: 'en-us',
  headline: " The World's Leading Online Art Gallery | Saatchi Art ",
  description: 'Discover and buy original artwork from emerging artists around the world.',
  isFamilyFriendly: 'True',
}

const organizationSchema: JsonLdSchema = {
  '@context': 'http://schema.org',
  '@type': 'Organization',
  name: 'Saatchi Art',
  url: 'https://www.saatchiart.com',
  logo: 'https://d3t95n9c6zzriw.cloudfront.net/common/saatchi-art-logo-1x.png',
  sameAs: [
    'https://www.youtube.com/c/Saatchiart',
    'https://www.facebook.com/saatchionline',
    'https://twitter.com/SaatchiArt',
    'https://www.instagram.com/saatchiart',
    'https://www.pinterest.com/saatchiart',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Support',
    availableLanguage: ['English'],
    url: 'https://support.saatchiart.com/hc/en-us/requests/new',
  },
}

type InitialState = {
  user: User
  shared: Shared
  page: Page
}

type AppGlobalSchemaProps = {
  initialProps?: PageInitialProps
  initialState: InitialState
  router?: NextRouter
}

const AppGlobalSchema: FC<AppGlobalSchemaProps> = ({ initialProps, initialState, router }) => {
  // WordPress (wpCms) pages supply their own JSON-LD from the CMS `<head>`
  // (rendered via `docHead` in `_document.tsx`), so skip the global fallback
  // schemas here to avoid duplicate WebSite/Organization/WebPage entities.
  const isWordpressRoute = (router?.route || '').includes('wpCms')
  if (isWordpressRoute) {
    return null
  }

  const { schema } = initialProps?.metaData || {}
  const { webpage: webpageSchema } = schema || {}

  const geoLocationMetaData = getGeoLocationMetaData(initialState)
  const urlPath = router?.asPath || getPagePath(initialState)
  const { storeLocale: currentStoreLocale } = geoLocationMetaData

  // Create a storeLocale valid url w/ only PAGE as a valid query param for the canonical
  const result = parse(urlPath)
  const { origin, pathname: path, query } = result
  let queryParams = ''

  let cleanPath = path
  /* Remove trailing SLASH for SEO
    Ex: `.com/stories/` => `.com/stories`
    We don't want duplicate content
  */
  if (cleanPath.match(/.*[/]$/)) {
    cleanPath = cleanPath.replace(/\/$/, '')
  }

  if (query.page) {
    queryParams = urlQuery({
      page: query.page,
    })
  }

  const serverCanonicalUrl = `${origin}${cleanPath}${queryParams}` // The server does NOT send back `storeLocale` - we will need to add manually
  // Guard the URL-derived (tainted) value before it flows into the JSON-LD
  // `url`: only emit well-formed http(s) URLs, rejecting javascript:/data:.
  const canonicalUrl = getSafeHttpUrl(
    cleanHomePage(createNewURL(serverCanonicalUrl, currentStoreLocale))
  )

  return (
    <Head>
      {!schema?.website && renderLDJSON(siteSchema, 'ld-website')}
      {!schema?.organization && renderLDJSON(organizationSchema, 'ld-organization')}
      {renderLDJSON({ ...pageSchema, ...webpageSchema, url: canonicalUrl }, 'ld-webpage')}
    </Head>
  )
}

export default AppGlobalSchema
